import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Users, UserCog, Briefcase, BookOpen, TrendingUp, AlertTriangle, Wallet, FileCheck } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  const stats = [
    { label: "Students", value: d?.students ?? 0, icon: Users, color: "bg-blue-50 text-brand" },
    { label: "Volunteers", value: d?.volunteers ?? 0, icon: UserCog, color: "bg-emerald-50 text-emerald-600" },
    { label: "Internships", value: d?.internships ?? 0, icon: Briefcase, color: "bg-amber-50 text-amber-600" },
    { label: "Test Series", value: d?.test_series ?? 0, icon: BookOpen, color: "bg-violet-50 text-violet-600" },
    { label: "Applications", value: d?.applications ?? 0, icon: FileCheck, color: "bg-rose-50 text-rose-600" },
    { label: "Exam Attempts", value: d?.attempts ?? 0, icon: TrendingUp, color: "bg-cyan-50 text-cyan-600" },
    { label: "Revenue", value: `₹${(d?.revenue ?? 0).toLocaleString()}`, icon: Wallet, color: "bg-slate-900 text-amber-400" },
    { label: "Cheating Logs", value: d?.cheating_total ?? 0, icon: AlertTriangle, color: "bg-red-50 text-red-600" },
  ];

  // Build chart data from recent payments by day
  const byDay = {};
  (d?.recent_payments || []).forEach((p) => {
    const day = (p.created_at || "").slice(0, 10);
    if (!day) return;
    byDay[day] = (byDay[day] || 0) + (p.status === "success" ? p.amount : 0);
  });
  const chart = Object.entries(byDay).sort().map(([date, amt]) => ({ date: date.slice(5), amt }));

  const examChart = [
    { name: "Attempts", value: d?.attempts ?? 0 },
    { name: "Apps", value: d?.applications ?? 0 },
    { name: "Violations", value: d?.cheating_total ?? 0 },
  ];

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Control Room</div>
        <h1 className="heading text-3xl font-bold mt-1">Admin Dashboard</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => {
          const I = s.icon;
          return (
            <div key={s.label} className="p-5 rounded-xl border border-slate-200 bg-white" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}><I size={18} /></div>
              <div className="heading mt-3 text-2xl font-bold">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-5">
        <div className="p-5 rounded-xl border border-slate-200 bg-white">
          <h2 className="heading text-lg font-bold">Revenue trend</h2>
          <div className="h-60 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="amt" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-5 rounded-xl border border-slate-200 bg-white">
          <h2 className="heading text-lg font-bold">Activity overview</h2>
          <div className="h-60 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={examChart}>
                <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip />
                <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-5">
        <div className="p-5 rounded-xl border border-slate-200 bg-white">
          <h2 className="heading text-lg font-bold">Recent Cheating Logs</h2>
          <div className="mt-3 divide-y text-sm">
            {(d?.recent_cheating_logs || []).slice(0, 6).map((l) => (
              <div key={l.id} className="py-3">
                <div className="font-semibold">{l.user_name} — <span className="text-red-600">{l.type}</span></div>
                <div className="text-xs text-slate-500">{l.details || "—"}</div>
                <div className="text-[10px] text-slate-400">{new Date(l.timestamp).toLocaleString()}</div>
              </div>
            ))}
            {(!d?.recent_cheating_logs || d.recent_cheating_logs.length === 0) && <div className="py-4 text-slate-500">No suspicious activity yet.</div>}
          </div>
        </div>
        <div className="p-5 rounded-xl border border-slate-200 bg-white">
          <h2 className="heading text-lg font-bold">Recent Payments</h2>
          <div className="mt-3 divide-y text-sm">
            {(d?.recent_payments || []).slice(0, 6).map((p) => (
              <div key={p.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">₹{p.amount}</div>
                  <div className="text-[10px] text-slate-400">{new Date(p.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase ${p.status === "success" ? "bg-emerald-100 text-emerald-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </div>
            ))}
            {(!d?.recent_payments || d.recent_payments.length === 0) && <div className="py-4 text-slate-500">No payments yet.</div>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
